import React from 'react';
import { Milestone } from '../../types/pms';
import ProgressSlider from './ProgressSlider';
import './MilestoneCard.css';

interface MilestoneCardProps {
  milestone: Milestone;
  onClick?: (milestone: Milestone) => void;
  onProgressChange?: (milestoneId: string, value: number) => void;
  progressEditable?: boolean;
}

/**
 * Format an ISO date string for display.
 */
function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/**
 * Check if a milestone is overdue (past due date with progress < 100%).
 */
function isOverdue(milestone: Milestone): boolean {
  return milestone.dueDate < new Date().toISOString() && milestone.progressPercentage < 100;
}

/**
 * MilestoneCard Component
 * Displays milestone title, due date, progress bar, and overdue indicator.
 * Requirements: 3.3, 3.5
 */
const MilestoneCard: React.FC<MilestoneCardProps> = ({ milestone, onClick, onProgressChange, progressEditable = false }) => {
  const overdue = isOverdue(milestone);

  const handleClick = () => {
    if (onClick) onClick(milestone);
  };

  return (
    <div
      className={`pms-milestone-card ${overdue ? 'pms-milestone-card--overdue' : ''}`}
      onClick={handleClick}
      style={onClick ? { cursor: 'pointer' } : undefined}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={
        onClick
          ? (e) => { if (e.key === 'Enter' || e.key === ' ') handleClick(); }
          : undefined
      }
    >
      <div className="pms-milestone-card__header">
        <h4 className="pms-milestone-card__title">{milestone.title}</h4>
        {overdue && (
          <span className="pms-milestone-card__overdue-badge">Overdue</span>
        )}
      </div>

      <span className="pms-milestone-card__due">
        Due: {formatDate(milestone.dueDate)}
      </span>

      <ProgressSlider
        value={milestone.progressPercentage}
        onChange={progressEditable ? (value) => onProgressChange?.(milestone.id, value) : undefined}
        readOnly={!progressEditable}
        size="sm"
        ariaLabel={`Milestone progress for ${milestone.title}`}
      />
    </div>
  );
};

export default MilestoneCard;
